import { AsYouType, parsePhoneNumberFromString, type CountryCode } from 'libphonenumber-js';
import { forwardRef } from 'react';
import { MaskedInput, type MaskedInputProps } from './masked-input.js';

export interface PhoneInputProps extends Omit<
  MaskedInputProps,
  'format' | 'badge' | 'hint' | 'hintTone'
> {
  /// O país de quem digita sem "+". Com "+", vale o código digitado.
  defaultCountry?: CountryCode | undefined;
}

const countryNames = new Intl.DisplayNames(['pt-BR'], { type: 'region' });

/// Telefone de qualquer país. Sem "+", é do país padrão e ganha a máscara dele — no Brasil, DDD com
/// 8 ou 9 dígitos; com "+", o país sai do código e a máscara também. Um número possível mas fora
/// da numeração do país não é recusado: o campo avisa e deixa salvar.
export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  ({ defaultCountry = 'BR', value, ...props }, ref) => {
    const phone = value.trim() ? parsePhoneNumberFromString(value, defaultCountry) : undefined;
    const typing = new AsYouType(defaultCountry);
    typing.input(value);
    const country = phone?.country ?? typing.getCountry();
    const status = phoneStatus(value, phone, country, defaultCountry);
    return (
      <div className="w-full min-w-0 max-w-sm">
        <MaskedInput
          ref={ref}
          inputMode="tel"
          autoComplete="tel"
          value={value}
          format={(digits, international) =>
            digits
              ? new AsYouType(defaultCountry).input(
                  `${international ? '+' : ''}${digits.slice(0, 15)}`,
                )
              : international
                ? '+'
                : ''
          }
          badge={country}
          hint={status.text}
          hintTone={status.tone}
          {...props}
        />
      </div>
    );
  },
);
PhoneInput.displayName = 'PhoneInput';

function phoneStatus(
  value: string,
  phone: ReturnType<typeof parsePhoneNumberFromString>,
  country: CountryCode | undefined,
  defaultCountry: CountryCode,
): { text: string; tone: 'muted' | 'positive' | 'warning' } {
  const name = (code: string) => countryNames.of(code) ?? code;
  if (!/\d/u.test(value)) {
    return {
      text: `Sem +, vale o DDD de ${name(defaultCountry)}. Com +, o país pelo código.`,
      tone: 'muted',
    };
  }
  if (phone?.isValid())
    return { text: name(phone.country ?? country ?? defaultCountry), tone: 'positive' };
  if (phone?.isPossible()) {
    const where = phone.country
      ? name(phone.country)
      : `código +${String(phone.countryCallingCode)}`;
    return {
      text:
        phone.country === 'BR'
          ? 'Confira o número: no Brasil são 8 dígitos (fixo) ou 9 (celular) depois do DDD.'
          : `Confira o número: não parece um telefone de ${where}.`,
      tone: 'warning',
    };
  }
  return { text: 'Número incompleto.', tone: 'muted' };
}
